import { flatMap } from 'es-toolkit';
import React from 'react';

import { pluralize } from '@paragon/utilities';

import { getCommittedComponentDesigns } from '../../../../store/selectors/getComponentDesignsSelectors';
import {
  canLongRunningAnalysisBeCanceled,
  isLongRunningAnalysisFinished,
} from '../../../../store/selectors/longRunningOperationSelectors';
import {
  cancelLongRunningAnalysis,
  clearLongRunningAnalysis,
} from '../../../../store/slices/longRunningOperationsSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { ComponentDesignInProgress } from '../ComponentDesignListItem';
import { LongRunningProgressDialog } from '../LongRunningProgressDialog';
import { describeAnalysisState } from './describeAnalysisState';

function getOverallStatusMessage(totalComponents: number, problems: { type: 'warning' | 'error' }[]) {
  const errors = problems.filter((p) => p.type === 'error').length;
  const warnings = problems.filter((p) => p.type === 'warning').length;

  let result = `${totalComponents} components`;
  if (errors > 0 || warnings > 0) result += '; ';

  if (errors > 0) {
    result += `${errors} ${pluralize('error', errors)}`;
    if (warnings > 0) result += ', ';
  }

  if (warnings > 0) {
    result += `${warnings} ${pluralize('warning', warnings)}`;
  }

  return result;
}

export function AnalyzeDialog() {
  const dispatch = useAppDispatch();

  const operation = useAppSelector((state) => state.longRunningOperations.analysis)!;
  const componentDesigns = useAppSelector(getCommittedComponentDesigns);

  const isFinished = useAppSelector(isLongRunningAnalysisFinished);
  const canBeCanceled = useAppSelector(canLongRunningAnalysisBeCanceled);

  function getComponentDesignName(guid: string) {
    const componentDesign = componentDesigns.find((design) => design.guid === guid);
    return componentDesign?.component.name ?? 'unknown';
  }

  const totalComponentCount = Object.keys(operation.componentDesigns).length;
  const recordedMessages = flatMap(Object.values(operation.componentDesigns), (design) => design.recordedMessages);

  const statusMessage = getOverallStatusMessage(totalComponentCount, recordedMessages);

  const componentDesignsInProgress = Object.entries(operation.componentDesigns).map(
    ([guid, state]): ComponentDesignInProgress => {
      const [category, statusMessage] = describeAnalysisState(state);
      return {
        name: getComponentDesignName(guid),
        category,
        statusMessage,
        problems: state.recordedMessages,
      };
    },
  );

  return (
    <LongRunningProgressDialog
      title="Analyzing Component Designs"
      finished={isFinished}
      statusMessage={statusMessage}
      componentDesigns={componentDesignsInProgress}
      onCancel={canBeCanceled ? () => void dispatch(cancelLongRunningAnalysis()) : undefined}
      onClose={() => void dispatch(clearLongRunningAnalysis())}
    />
  );
}
